'use client';
import { SessionProvider } from 'next-auth/react';
import { ReactNode, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AuthEffects from '@core/ui/auth/AuthEffects';
import { enableDebug, initializeDebug } from '@core/be/platform/logging/Logger';
import { useRuntimeConfig } from '@core/ui/hooks/useRuntimeConfig';
import { ToastProvider } from '@/core/ui/primitives/toast';
import { AppToastService } from '@/core/ui/primitives/ToastService';
import { ProfileOrganizationProvider } from '@core/ui/profile/ProfileOrganizationProvider';
import { OrgContextProvider } from '@organizations/ui/OrgContext';
import { setKrokiBaseUrl } from '@core/ui/widgets/diagram/kroki';
import { preventScrollbarCompensation } from '@/core/helpers/misc/preventScrollbarCompensation';
import { ActionTrailProvider } from '@core/ui/telemetry/ActionTrailProvider';
import { reportClientError } from '@core/ui/telemetry/reportClientError';
import { AppReadyProvider } from '@core/helpers/react/contexts/AppReadyContext';
interface ProvidersProps {
  children: ReactNode;
}
function ExtensionErrorHandler() {
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      if (
        reason instanceof Error &&
        reason.message.includes('message channel closed') &&
        reason.message.includes('asynchronous response')
      ) {
        // Suppress Chrome extension errors
        event.preventDefault();
        return;
      }
      const message = reason instanceof Error ? reason.message : String(reason);
      const lowered = message.toLowerCase();
      // Heuristic: toast only for network / request-like failures.
      if (lowered.includes('fetch') || lowered.includes('network') || lowered.includes('request')) {
        AppToastService.error(message, 'Request error');
      }

      void reportClientError({
        source: 'window.unhandledrejection',
        error: reason instanceof Error ? reason : new Error(message),
        extra: { kind: 'unhandledrejection' },
      });
    };
    const handleError = (event: ErrorEvent) => {
      const error = event.error;
      if (
        error instanceof Error &&
        error.message.includes('message channel closed') &&
        error.message.includes('asynchronous response')
      ) {
        // Suppress Chrome extension errors
        event.preventDefault();
        return;
      }
      const message = error instanceof Error ? error.message : event.message;
      const lowered = message.toLowerCase();
      if (lowered.includes('fetch') || lowered.includes('network') || lowered.includes('request')) {
        AppToastService.error(message, 'Request error');
      }

      void reportClientError({
        source: 'window.error',
        error: error instanceof Error ? error : new Error(message),
        extra: { kind: 'error' },
      });
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);
  return null;
}
function DebugBootstrap() {
  const { config } = useRuntimeConfig('authenticated');
  useEffect(() => {
    // Apply server-provided debug namespaces when available
    const namespaces = config?.debug?.namespaces;
    if (Array.isArray(namespaces) && namespaces.length > 0) {
      enableDebug(namespaces.join(','));
    }
    if (config?.diagram?.krokiBaseUrl) {
      setKrokiBaseUrl(config.diagram.krokiBaseUrl);
    }
    const result = initializeDebug();
    // eslint-disable-next-line no-console
    console.info('[mindmap][debug] init', result);
  }, [config?.debug?.namespaces, config?.diagram?.krokiBaseUrl]);
  return null;
}
export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60000,
            gcTime: 10 * 60000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      })
  );

  // Prevent Radix UI and other libraries from adding scrollbar compensation
  useEffect(() => {
    preventScrollbarCompensation();
  }, []);

  return (
    <SessionProvider refetchInterval={0} refetchOnWindowFocus={false} refetchWhenOffline={false}>
      <QueryClientProvider client={queryClient}>
        <AppReadyProvider>
          <OrgContextProvider>
            <ProfileOrganizationProvider>
              <ToastProvider>
                <ActionTrailProvider>
                  <DebugBootstrap />
                  <ExtensionErrorHandler />
                  <AuthEffects />
                  {children}
                </ActionTrailProvider>
              </ToastProvider>
            </ProfileOrganizationProvider>
          </OrgContextProvider>
        </AppReadyProvider>
      </QueryClientProvider>
    </SessionProvider>
  );
}
